import * as React from "react";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  FilledInput,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { formatLastDayMinute } from "./AlertHoursPicker";

export type NumberOption = { value: number; label: string };

type CompactHoursSelectProps = {
  value: number;
  onChange: (value: number) => void;
  options: NumberOption[];
  minValue?: number;
  disabled?: boolean;
  renderValue: (value: number) => React.ReactNode;
  height?: number;
  menuMaxHeight?: number;
  width?: number;
  sx?: SxProps<Theme>;
};

export default function CompactHoursSelect({
  value,
  onChange,
  options,
  minValue,
  disabled,
  renderValue,
  height = 32,
  width = 75,
  menuMaxHeight = 200,
  sx,
  ...props
}: CompactHoursSelectProps) {
  const theme = useTheme();
  const handleChange = (e: SelectChangeEvent<number>) => {
    onChange(Number(e.target.value));
  };

  return (
    <FormControl size="small">
      <Select<number>
        variant="filled"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        IconComponent={() => null}
        input={<FilledInput disableUnderline />}
        sx={{
          width,
          height,
          boxSizing: "border-box",
          cursor: "pointer",
          m: 0,
          backgroundColor: theme.palette.otherBackground.main,
          borderRadius: "4px",
          color: theme.palette.text.secondary,
          border: `1px solid transparent`,
          "& .MuiSelect-select": {
            width,
            height,
            paddingRight: "3px !important",
            padding: "1px 4px",
            borderRadius: "4px",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
          },
          "&.Mui-focused": {
            borderColor: theme.palette.primary.main,
            width,
            height,
          },
          ...sx,
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: menuMaxHeight, borderRadius: "8px", p: 0 },
          },
          MenuListProps: { dense: true, sx: { p: 0 } },
        }}
        renderValue={v => {
          const correctLastDayMinute = formatLastDayMinute(v);

          return renderValue(correctLastDayMinute);
        }}
        {...props}
      >
        {options.map(o => (
          <MenuItem
            key={o.value}
            value={o.value}
            disabled={o.value < (minValue ?? 0)}
            sx={{
              position: "relative",
              minHeight: "auto",
              height: "2rem",
              py: 0.5,
              lineHeight: 1.2,
              "&:hover": {
                backgroundColor: theme =>
                  theme.palette?.primary?.shades?.["8p"] ?? "rgba(0,0,0,0.04)",
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: theme => theme.palette.primary.main,
                },
              },
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
